import { useAuth } from "@clerk/clerk-react";
import { Box } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  FollowUpQuestionOption,
  NicheFollowUpQuestion,
  NicheFollowUpResponse,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import MultiChipSelect from "components/Styled/MultiChipSelect";
import StyledVaPage from "components/Styled/VAPage";
import { fetcherAuth } from "utils/api";
import { useTracking } from "utils/useTracking";

interface Props {
  nicheFollowUpQuestions: NicheFollowUpQuestion[];
  nicheFollowUpResponses: NicheFollowUpResponse[];
  setFollowUpNicheResponses: Dispatch<SetStateAction<NicheFollowUpResponse[]>>;
  finishSetUpLater: () => void;
}

const SelfServeNicheFollowUpQuestionPage = ({
  nicheFollowUpQuestions,
  nicheFollowUpResponses,
  setFollowUpNicheResponses,
  finishSetUpLater,
}: Props) => {
  const { trackVaStep } = useTracking();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { organizationVaProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const [followUpQuestions, setFollowUpQuestions] = useState<
    NicheFollowUpQuestion[]
  >([]);
  const [selectedFollowupAnswers, setSelectedFollowupAnswers] = useState<{
    [key in number]: FollowUpQuestionOption[];
  }>({});
  const [nicheToQuestionIdMap, setNicheToQuestionIdMap] = useState<{
    [key in number]: number;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(-1);
  const [error, setError] = useState("");
  const hasInitializedActiveIndex = useRef(false);

  const initializeQuestions = () => {
    const questionsToAsk: NicheFollowUpQuestion[] = [];
    const nicheToQuestionIdMap: { [key in number]: number } = {};
    for (const niche of organizationVaProfile!.niches!) {
      const followUpQuestion = nicheFollowUpQuestions?.find(
        (question) =>
          question.triggerBentoCategoryId === niche?.bentoCategoryId,
      );
      if (followUpQuestion) {
        questionsToAsk.push(followUpQuestion);
        nicheToQuestionIdMap[niche.id!] = followUpQuestion.id;
      }
    }
    setFollowUpQuestions(questionsToAsk);
    setNicheToQuestionIdMap(nicheToQuestionIdMap);
  };

  const initializeResponses = () => {
    const prefilledAnswers: {
      [key in number]: FollowUpQuestionOption[];
    } = {};
    for (const response of nicheFollowUpResponses) {
      prefilledAnswers[response.nicheFollowUpQuestionId] =
        response.selectedOptions;
    }

    if (Object.keys(prefilledAnswers).length > 0) {
      setSelectedFollowupAnswers(prefilledAnswers);
    }
  };

  const updateNiches = async () => {
    const allRequiredQuestionsAnswered = followUpQuestions.every(
      (question) =>
        !question.isRequired ||
        question.id.toString() in selectedFollowupAnswers,
    );

    if (!allRequiredQuestionsAnswered) {
      setError("Please answer all required questions before proceeding.");
      return;
    }

    setLoading(true);
    const nichesToUpdate: {
      id: number;
      followUpResponses: { [key in number]: FollowUpQuestionOption[] };
    }[] = [];

    for (const key in nicheToQuestionIdMap) {
      const questionId = nicheToQuestionIdMap[key];
      nichesToUpdate.push({
        id: parseInt(key),
        followUpResponses: {
          [questionId]: selectedFollowupAnswers[questionId],
        },
      });
    }
    try {
      const { niches: newNiches } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/from-onboarding`,
        "PUT",
        {},
        {
          niches: nichesToUpdate,
        },
      );
      navigate(`/${routes.templatesSelfServeOnboarding}`);
      trackVaStep(routes.templatesSelfServeOnboarding);

      const responses = [];
      if (newNiches?.length > 0) {
        for (const n of newNiches) {
          for (const r of n.followUpResponses) {
            if (r.nicheFollowUpQuestionId) {
              responses.push(r);
            }
          }
        }
      }

      setFollowUpNicheResponses(responses);
      setError("");
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
      navigate(`/${routes.nicheSelfServeOnboarding}`);
    } finally {
      setLoading(false);
    }
  };

  const nicheIds = organizationVaProfile?.niches?.map((niche) => niche.id);
  const stringifyId = (nicheIds || []).sort().join(",");
  useEffect(() => {
    if (
      organizationVaProfile?.niches &&
      organizationVaProfile?.niches?.length > 0 &&
      nicheFollowUpQuestions &&
      nicheFollowUpQuestions?.length > 0
    ) {
      initializeQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stringifyId, nicheFollowUpQuestions?.length]);

  useEffect(() => {
    if (nicheFollowUpResponses?.length > 0) {
      initializeResponses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nicheFollowUpResponses?.length]);

  useEffect(() => {
    if (followUpQuestions.length > 0 && !hasInitializedActiveIndex.current) {
      hasInitializedActiveIndex.current = true;
      const firstUnanswered = followUpQuestions.findIndex(
        (q) => q.isRequired && !(selectedFollowupAnswers[q.id]?.length > 0),
      );
      setActiveQuestionIndex(
        firstUnanswered >= 0 ? firstUnanswered : followUpQuestions.length,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followUpQuestions, selectedFollowupAnswers]);

  const optionIsSelected = (
    followUpQuestion: NicheFollowUpQuestion,
    option: FollowUpQuestionOption,
  ) => {
    return selectedFollowupAnswers[followUpQuestion.id]?.some(
      (selectedOption) => selectedOption.option === option.option,
    );
  };

  return (
    <StyledVaPage
      splitScreen
      stepNumber={2}
      annotation="(cont.)"
      title="Additional questions on your niches 🔍"
      description="These answers help us unlock more personalized and relevant brand opportunities"
      time="2-3"
      body={
        <Box>
          {followUpQuestions?.map((followUpQuestion, index) => (
            <Box key={followUpQuestion.id} sx={{ mt: 2 }}>
              <MultiChipSelect
                label={followUpQuestion.question}
                description={
                  followUpQuestion.description || "Select all that apply"
                }
                options={followUpQuestion.options.map(
                  (category: FollowUpQuestionOption) => ({
                    id: category.option,
                    label: category.option,
                  }),
                )}
                completed={
                  followUpQuestion.isRequired
                    ? !!selectedFollowupAnswers[followUpQuestion.id]?.length
                    : true
                }
                isOpen={activeQuestionIndex === index}
                onRequestOpen={() => setActiveQuestionIndex(index)}
                onCollapse={() => setActiveQuestionIndex(index + 1)}
                onClickOption={(chip: any) => {
                  const option = followUpQuestion.options.find(
                    (opt) => opt.option === chip.id,
                  );
                  if (!option) return;

                  const isSelected = optionIsSelected(followUpQuestion, option);
                  if (isSelected) {
                    setSelectedFollowupAnswers((prev) => {
                      const copy = { ...prev };
                      copy[followUpQuestion.id] = copy[
                        followUpQuestion.id
                      ].filter((v) => v.option !== option.option);
                      return copy;
                    });
                  } else {
                    setSelectedFollowupAnswers((prev) => {
                      const copy = { ...prev };
                      copy[followUpQuestion.id] = [
                        ...(copy[followUpQuestion.id] || []),
                        option,
                      ];
                      return copy;
                    });
                  }
                  setError("");
                }}
                selectedOptionsIds={
                  selectedFollowupAnswers[followUpQuestion.id]?.map(
                    (option) => option.option,
                  ) || []
                }
                error={activeQuestionIndex === index ? error : ""}
              />
            </Box>
          ))}
        </Box>
      }
      buttonBack={"Back"}
      onBack={() => {
        trackVaStep(routes.nicheSelfServeOnboarding);
        navigate(`/${routes.nicheSelfServeOnboarding}`);
      }}
      buttonNext="Continue To Templates"
      onNext={updateNiches}
      onFinishSetUpLater={finishSetUpLater}
      loading={loading}
    />
  );
};

export default SelfServeNicheFollowUpQuestionPage;
